import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Links, Nodes, NodesState } from "./nodes.model";

const initialState: NodesState = {
  nodes: [],
  links: [],
  plotWidth: 0,
  plotHeight: 0,
  selectedPlotWidth: 0,
  selectedPlotHeight: 0,
};

export const nodesSlice = createSlice({
  name: "nodesState",
  initialState,
  reducers: {
    updateAddNodes: (state, action: PayloadAction<Nodes>) => {
      state.nodes.push(action.payload);
    },

    updateAddLinks: (state, action: PayloadAction<Links>) => {
      state.links.push(action.payload);
    },

    updateResetBoard: (state) => {
      state.nodes = [];
      state.links = [];
    },

    updateNodeLabel: (
      state,
      action: PayloadAction<{ id: number; label: string }>
    ) => {
      const index = state.nodes.findIndex(
        (node) => node.id === action.payload.id
      );
      if (index !== -1) {
        state.nodes[index].label = action.payload.label;
      }
    },

    updateNodePosition: (
      state,
      action: PayloadAction<{ id: number; x: number; y: number }>
    ) => {
      const index = state.nodes.findIndex(
        (node) => node.id === action.payload.id
      );

      if (index !== -1) {
        state.nodes[index].x = action.payload.x;
        state.nodes[index].y = action.payload.y;
      }
    },

    updateNodeWidth: (
      state,
      action: PayloadAction<{ id: number; max: number; min: number }>
    ) => {
      const index = state.nodes.findIndex(
        (node) => node.id === action.payload.id
      );
      if (index !== -1) {
        state.nodes[index].width.max = action.payload.max;
        state.nodes[index].width.min = action.payload.min;
      }
    },

    updateNodeHeight: (
      state,
      action: PayloadAction<{ id: number; max: number; min: number }>
    ) => {
      const index = state.nodes.findIndex(
        (node) => node.id === action.payload.id
      );
      if (index !== -1) {
        state.nodes[index].height.max = action.payload.max;
        state.nodes[index].height.min = action.payload.min;
      }
    },

    updateNodeRatio: (
      state,
      action: PayloadAction<{ id: number; max: number; min: number }>
    ) => {
      const index = state.nodes.findIndex(
        (node) => node.id === action.payload.id
      );
      if (index !== -1) {
        state.nodes[index].ratio.max = action.payload.max;
        state.nodes[index].ratio.min = action.payload.min;
      }
    },

    updateNodeRoomType: (
      state,
      action: PayloadAction<{ id: number; roomtype?: string }>
    ) => {
      const index = state.nodes.findIndex(
        (node) => node.id === action.payload.id
      );

      if (index !== -1) {
        state.nodes[index].roomtype = action.payload.roomtype;
      }
    },

    deleteNode: (state, action: PayloadAction<number>) => {
      const nodeIdToDelete = action.payload;

      // Remove node
      state.nodes = state.nodes.filter((node) => node.id !== nodeIdToDelete);

      // Remove links connected to node
      state.links = state.links.filter(
        (link) =>
          link.source !== nodeIdToDelete && link.target !== nodeIdToDelete
      );

      // Reindex nodes
      state.nodes = state.nodes.map((node) => {
        if (node.id > nodeIdToDelete) {
          return { ...node, id: node.id - 1 };
        }
        return node;
      });

      // Reindex links
      state.links = state.links.map((link) => {
        let newSource = link.source;
        let newTarget = link.target;

        if (link.source > nodeIdToDelete) newSource--;
        if (link.target > nodeIdToDelete) newTarget--;

        return { ...link, source: newSource, target: newTarget };
      });
    },

    updateLinks: (state, action: PayloadAction<Links[]>) => {
      state.links = action.payload;
    },

    updateNodes: (state, action: PayloadAction<Nodes[]>) => {
      state.nodes = action.payload;
    },

    resetNodesState: (state) => {
      state.nodes = [];
      state.links = [];
    },

    updatePlotWidth: (state, action: PayloadAction<number>) => {
      state.plotWidth = action.payload;
    },

    updatePlotHeight: (state, action: PayloadAction<number>) => {
      state.plotHeight = action.payload;
    },

    updateSelectedPlotWidth: (state, action: PayloadAction<number | null>) => {
      state.selectedPlotWidth = action.payload ?? 0;
    },

    updateSelectedPlotHeight: (state, action: PayloadAction<number | null>) => {
      state.selectedPlotHeight = action.payload ?? 0;
    },

    reset: () => initialState,
  },
});

export const {
  updateAddNodes,
  updateAddLinks,
  updateResetBoard,
  updateNodeHeight,
  updateNodeRatio,
  updateNodeWidth,
  updateNodeLabel,
  updateNodePosition,
  updateNodeRoomType,
  deleteNode,
  resetNodesState,
  updateLinks,
  updatePlotWidth,
  updatePlotHeight,
  updateSelectedPlotWidth,
  updateSelectedPlotHeight,
  reset,
  updateNodes,
} = nodesSlice.actions;

export default nodesSlice.reducer;

